import { useCallback } from 'react';
import type { ReservationsSearch, ReservationsSearchChange } from '../lib/reservations-list-search';

export interface ReservationsListHandlers {
  myReservationsActive: boolean;
  toggleHasQueue: () => void;
  toggleMyReservations: () => void;
  changePage: (page: number) => void;
  changeFilter: (values: Record<string, string[]>) => void;
  changeSort: (key: string) => void;
}

// The dateRange filter uses a combined [from, to] array; split it back into the URL params.
function filterValuesToPatch(values: Record<string, string[]>): Partial<ReservationsSearch> {
  const { dateRange, ...rest } = values;
  const dateFrom = dateRange != null ? (dateRange[0] ?? '') : undefined;
  const dateTo = dateRange != null ? (dateRange[1] ?? '') : undefined;
  return {
    ...rest,
    ...(dateFrom !== undefined ? { dateFrom: dateFrom !== '' ? dateFrom : undefined } : {}),
    ...(dateTo !== undefined ? { dateTo: dateTo !== '' ? dateTo : undefined } : {}),
    offset: 0,
  };
}

export function useReservationsListHandlers(
  search: ReservationsSearch,
  onSearchChange: ReservationsSearchChange,
  currentUserId: number,
): ReservationsListHandlers {
  // A manual, always-visible self-scope for ANY caller, distinct from the sales-role auto-scope.
  const myReservationsActive =
    search.holderUserId.length === 1 && search.holderUserId[0] === String(currentUserId);

  const toggleHasQueue = useCallback(() => {
    onSearchChange({ hasQueue: !search.hasQueue, offset: 0 });
  }, [onSearchChange, search.hasQueue]);

  const toggleMyReservations = useCallback(() => {
    onSearchChange({
      holderUserId: myReservationsActive ? [] : [String(currentUserId)],
      offset: 0,
    });
  }, [onSearchChange, myReservationsActive, currentUserId]);

  const changePage = useCallback(
    (page: number) => onSearchChange({ offset: (page - 1) * search.limit }),
    [onSearchChange, search.limit],
  );

  const changeFilter = useCallback(
    (values: Record<string, string[]>) => onSearchChange(filterValuesToPatch(values)),
    [onSearchChange],
  );

  const changeSort = useCallback(
    (key: string) => {
      onSearchChange({
        sort: key as ReservationsSearch['sort'],
        order: search.sort === key && search.order === 'asc' ? 'desc' : 'asc',
        offset: 0,
      });
    },
    [onSearchChange, search.sort, search.order],
  );

  return {
    myReservationsActive,
    toggleHasQueue,
    toggleMyReservations,
    changePage,
    changeFilter,
    changeSort,
  };
}
